/**
 * SweepstakesDataMapper.js
 * Transforms API response structure to component-friendly format
 */

/**
 * Calculate status badge text from progress
 * @param {number} completed - Number of completed steps
 * @param {number} total - Total number of steps
 * @param {boolean} isOptedIn - Whether user is opted in
 * @returns {string} Status text for badge
 */
export const calculateStatus = (completed = 0, total = 0, isOptedIn = false) => {
  if (!isOptedIn || completed === 0) return 'New';
  return `${completed} of ${total} Earned`;
};

/**
 * Map API task to component step format
 * @param {Object} task - Task from API response
 * @param {string} globalIcon - Global step tracker icon URL
 * @returns {Object} Component-friendly step object
 */
const mapTaskToStep = (task, globalIcon) => {
  if (!task) return null;

  const stepMetaData = task.taskMetaData || task.stepMetaData || null;
  const ctaHref = stepMetaData?.ctaUrl || stepMetaData?.surveyUrl || null;
  const ctaTitle = stepMetaData?.ctaDescription || task.name || null;
  const eventBusTrigger = stepMetaData?.ctaAction || task.eventBusTrigger || null;

  return {
    id: task.customerTaskId || task.name,
    title: task.name,
    subtitle: stepMetaData?.subTitle || null,
    description: task.description || null,
    iconUrl: globalIcon || 'https://placehold.co/32x32/000000/FFFFFF?text=%23',
    completed: task.status === 'COMPLETED',
    ctaTitle,
    ctaHref,
    eventBusTrigger,
    stepMetaData,
  };
};

/**
 * Map API response to SweepstakesEntryCard props
 * @param {Object} apiResponse - Raw API response
 * @returns {Object} Component-friendly data structure
 */
export const mapSweepstakesData = (apiResponse) => {
  if (!apiResponse) return null;

  const {
    challengeId,
    customerChallengeId,
    v2Challenge,
    name,
    description,
    imageUrl,
    challengeEndDate,
    termsAndConditions,
    optedStatus,
    activityStatus,
    challengeStepsCompleted,
    challengeStepsTotal,
    tasks,
    challengeMetaData,
  } = apiResponse;
  // Convert challengeMetaData to object format
  // API may return as Map scalar, array, or already as object
  // Convert to object: {type: "SWEEPSTAKES", ...}
  let metadata = {};

  if (Array.isArray(challengeMetaData)) {
    // Legacy format: [{key: "type", value: "SWEEPSTAKES"}, ...]
    metadata = challengeMetaData.reduce((acc, item) => {
      acc[item.key] = item.value;
      return acc;
    }, {});
  } else if (typeof challengeMetaData === 'object' && challengeMetaData !== null) {
    // Object format (mock data or already processed)
    metadata = challengeMetaData;
  } else if (typeof challengeMetaData === 'string') {
    // Handle potential JSON string from Map scalar
    try {
      metadata = JSON.parse(challengeMetaData);
    } catch {
      metadata = {};
    }
  } else {
    metadata = {};
  }

  const isOptedIn = optedStatus === 'OPTED_IN';

  // Extract and sort checkbox keys once
  const checkboxKeys = Object.keys(metadata)
    .filter((key) => key.startsWith('preOptInDrawer_checkbox_'))
    .sort((aKey, bKey) => {
      const numA = parseInt(aKey.split('_').pop(), 10);
      const numB = parseInt(bKey.split('_').pop(), 10);
      return numA - numB;
    });

  // Extract all checkbox values
  const optInCheckboxes = checkboxKeys.map((key) => metadata[key]);

  // Separate required checkboxes (exclude checkbox_1 which is for email marketing consent)
  const requiredCheckboxIndices = checkboxKeys
    .filter((key) => !key.endsWith('_1'))
    .map((key) => {
      const checkboxNum = parseInt(key.split('_').pop(), 10);
      return checkboxNum - 1;
    });

  // Map tasks to steps with null checking
  const mappedSteps = tasks && Array.isArray(tasks)
    ? tasks.map((task) => mapTaskToStep(task, metadata.stepTrackerIcon)).filter(Boolean)
    : [];

  return {
    // Core identifiers
    challengeId,
    id: challengeId || customerChallengeId,
    customerChallengeId,
    isV2: v2Challenge,

    // Display content
    title: name,
    drawerDescription: description,
    cardDescription: metadata.sweepstakesCard_description,
    logo: imageUrl,
    heroImage: metadata.drawerBannerImage,

    // Status and progress
    status: calculateStatus(challengeStepsCompleted, challengeStepsTotal, isOptedIn),
    isOptedIn,
    activityStatus: activityStatus || null,
    completedSteps: challengeStepsCompleted || 0,
    totalSteps: challengeStepsTotal || 0,

    // Date information
    expirationDate: challengeEndDate,

    // Bonus entry fields
    bonusEntryEligible: metadata.bonusEntryEligible === 'true',
    messageComplete: metadata.bonusEntry_postText,
    messageNotComplete: metadata.bonusEntry_preText,
    bonusEntryImage: metadata.bonusEntry_img,

    // Steps/tasks
    steps: mappedSteps,
    stepTrackerIcon: metadata.stepTrackerIcon,

    // Terms and drawer content
    termsAndConditions,

    // Opt-in drawer content
    preOptInDrawerTitle: metadata.preOptInDrawer_title,
    preOptInDrawerDescription: metadata.preOptInDrawer_description,
    postOptInDrawerDescription: metadata.postOptInDrawer_description,
    preOptInDrawerCta: metadata.preOptInDrawer_cta,
    optInCheckboxes,
    requiredCheckboxIndices,

    // Details drawer content
    stepTrackerLearnMoreText: metadata.stepTracker_learnMore_text || metadata.stepTracker_leanrMore_text,
    stepTrackerPostOptInDescription: metadata.stepTracker_postOptIn_preRequistie_drawer_description,
    allEntriesCompletedText: metadata.all_entries_completed_text,

    // Progress bar CTA labels
    inProgressCta: metadata.inProgress_cta || 'Earn More Entries',
    completedCta: metadata.completed_cta || 'Review Entries',
    inProgressCardDescription: metadata.sweepstakesCard_inProgress_description || null,
    completedCardDescription: metadata.sweepstakesCard_completed_description || null,

    // Additional metadata
    sweepstakesFaqs: metadata['Sweeps FAQ\'s'],
    welcomeCardTitle: metadata.welcomeCard_title,

    // CTA information (calculated)
    ctaTitle: isOptedIn ? null : (metadata.preOptInDrawer_cta || 'Take Survey'),
    ctaHref: null,
    ctaOnclick: null,
  };
};

/**
 * Map multiple sweepstakes from API response
 * @param {Object} apiResponse - API response with challenges array
 * @returns {Array} Array of mapped sweepstakes data
 */
export const mapSweepstakesArray = (apiResponse) => {
  if (!apiResponse || !apiResponse.challenges) return [];

  return apiResponse.challenges
    .map((challenge) => mapSweepstakesData(challenge))
    .filter(Boolean);
};

/**
 * Get first active sweepstakes from API response
 * @param {Object} apiResponse - API response with challenges array
 * @returns {Object|null} First active sweepstakes or null
 */
export const getFirstActiveSweepstakes = (apiResponse) => {
  const mapped = mapSweepstakesArray(apiResponse);
  return mapped.length > 0 ? mapped[0] : null;
};
